"use client"

import { ControlledModal, toast } from "@/components"
import { graphql } from "@/gql"
import {
  ClientPortfolioDocument,
  ReorderProjectsModalDocument,
  SetCreativeProfileProjectProfileOrderDocument,
} from "@/gql/graphql"
import { revalidatePathHelper } from "@/utils/revalidatePathHelper"
import { useMutation, useQuery } from "@apollo/client"
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core"
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { captureException } from "@sentry/nextjs"
import { FC, useEffect, useMemo, useState } from "react"
import { ReorderModalItemSkeleton } from "./ReorderProjectModalItemSkeleton"
import { ReorderProjectsModalItem } from "./ReorderProjectsModalItem"
import { ampli } from "@/ampli"

interface Props {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  creativeHandle: string
}

graphql(`
  mutation SetCreativeProfileProjectProfileOrder($input: SetCreativeProfileProjectOrderInput!) {
    setCreativeProfileProjectOrder(input: $input) {
      creative {
        __typename
        id
      }
      errors {
        field
        messages
      }
    }
  }
`)

graphql(`
  query ReorderProjectsModal($handle: String!) {
    creativeByHandle(handle: $handle) {
      id
      __typename
      creativeWorkPortfolioProjects(order: { profileOrder: ASC }, filters: { status: { exact: PUBLISHED } }) {
        edges {
          node {
            id
            ...ReorderProjectsModalItem
          }
        }
      }
    }
  }
`)

export const ReorderProjectsModal: FC<Props> = ({ isOpen, onOpenChange, creativeHandle }) => {
  const { data } = useQuery(ReorderProjectsModalDocument, {
    variables: {
      handle: creativeHandle,
    },
    context: {
      version: 2,
    },
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  })
  const [isUpdating, setIsUpdating] = useState(false)
  const [items, setItems] = useState<string[]>([])
  const [setOrder] = useMutation(SetCreativeProfileProjectProfileOrderDocument)

  const projects = useMemo(() => data?.creativeByHandle?.creativeWorkPortfolioProjects?.edges || [], [data])

  useEffect(() => {
    if (projects?.length > 0 && items.length === 0) {
      const projectIds = projects.map(edge => edge?.node?.id).filter(Boolean) as string[]
      setItems(projectIds)
    }
  }, [projects, items])

  const sensors = useSensors(useSensor(PointerSensor))

  const handleDragEnd = ({ active, over }: { active: any; over: any }) => {
    if (over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id)
      const newIndex = items.indexOf(over.id)
      setItems(items => arrayMove(items, oldIndex, newIndex))
    }
  }

  const onUpdate = async () => {
    if (isUpdating) return
    setIsUpdating(true)
    const response = await setOrder({
      context: {
        version: 2,
      },
      variables: {
        input: {
          creativeId: data?.creativeByHandle?.id!,
          projects: items.map(id => ({ id })),
        },
      },
      refetchQueries: [ClientPortfolioDocument],
    })

    const errors = response.data?.setCreativeProfileProjectOrder?.errors

    if (errors && errors.length > 0) {
      toast.error("Error updating project order")
      captureException(errors)
      setIsUpdating(false)
    } else {
      revalidatePathHelper(`/${creativeHandle}`)
      ampli.reorderWorkComplete()
      setIsUpdating(false)
      onOpenChange(false)
    }
  }

  const orderedProjects = useMemo(() => {
    const idToProjectMap = projects.reduce(
      (map, edge) => {
        if (edge?.node?.id) {
          map[edge.node.id] = edge.node
        }
        return map
      },
      {} as Record<string, NonNullable<(typeof projects)[number]>["node"]>
    )

    return items.map(id => idToProjectMap[id]).filter(Boolean)
  }, [items, projects])

  return (
    <ControlledModal
      open={isOpen}
      onOpenChange={onOpenChange}
      header={{ title: "Reorder projects", variant: "default", fixed: true }}
      body={
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto pb-4">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
              {orderedProjects && orderedProjects.length > 0
                ? orderedProjects.map(project => {
                    return <ReorderProjectsModalItem project={project} key={project.id} />
                  })
                : Array.from({ length: 5 }).map((_, index) => <ReorderModalItemSkeleton key={index} />)}
            </SortableContext>
          </DndContext>
        </div>
      }
      footer={{
        fixed: true,
        tertiaryButton: {
          children: "Cancel",
          onClick: () => onOpenChange(false),
        },
        primaryButton: {
          children: "Update",
          onClick: onUpdate,
          loading: isUpdating,
        },
      }}
    />
  )
}
